import { Check, TriangleAlert, Undo2 } from "lucide-react";
import { useMemo } from "react";

import { ColocaAmountDisplay } from "@/shared/components/data-display/ColocaAmountDisplay";
import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import {
  ColocaDataTable,
  type ColocaDataTableColumn,
} from "@/shared/components/data-display/ColocaDataTable";
import { ColocaStatusPill } from "@/shared/components/data-display/ColocaStatusPill";
import { colocaSurfaceVariants } from "@/shared/components/layout/colocaSurfaceVariants";
import useTranslations from "@/shared/lang/useTranslations";
import { formatDateTime } from "@/shared/utils/dateTimeHelpers";

import type { LedgerLabels } from "../hooks/useLedgerLabels";
import { LedgerDirection, type LedgerEntryGroup, type LedgerLeg } from "../types/paymentsLedger.types";
import { LedgerAccountCell } from "./LedgerAccountCell";
import { LedgerCopyableId } from "./LedgerCopyableId";

export interface LedgerEntryCardProps {
  entry: LedgerEntryGroup;
  labels: LedgerLabels;
  isMobile: boolean;
}

/** Money renders with the real decimals it was posted with: 2 minimum, up to the ledger's 8, never rounded up. */
const AMOUNT_FRACTIONS = { minimumFractionDigits: 2, maximumFractionDigits: 8 } as const;

/**
 * One journal entry (asiento).
 *
 * Debits and credits sit in two separate columns, as a journal is read everywhere else in accounting: the
 * amount goes in the column of its `direction`, unsigned, and the column totals per currency close the card.
 * That is deliberate — signing the amount by direction would be wrong, since a debit increases an asset and
 * decreases a liability, so the same sign would mean opposite things depending on the account.
 *
 * `balance_after` is the balance of THAT account after the leg posted, straight from the backend. It is never
 * recomputed here.
 */
export function LedgerEntryCard({ entry, labels, isMobile }: LedgerEntryCardProps) {
  const { t, activeLanguage } = useTranslations("paymentsLedger");

  const columns = useMemo<ColocaDataTableColumn<LedgerLeg>[]>(
    () => [
      {
        id: "account",
        header: t("th_account"),
        cell: (leg) => <LedgerAccountCell account={leg.account} labels={labels} />,
      },
      {
        id: "debit",
        header: t("th_debit"),
        headerClassName: "text-right",
        cellClassName: "text-right",
        cell: (leg) =>
          leg.direction === LedgerDirection.DEBIT ? (
            <ColocaAmountDisplay
              value={leg.amount}
              currency={leg.currency}
              size="sm"
              locale={activeLanguage}
              {...AMOUNT_FRACTIONS}
            />
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        id: "credit",
        header: t("th_credit"),
        headerClassName: "text-right",
        cellClassName: "text-right",
        cell: (leg) =>
          leg.direction === LedgerDirection.CREDIT ? (
            <ColocaAmountDisplay
              value={leg.amount}
              currency={leg.currency}
              size="sm"
              locale={activeLanguage}
              {...AMOUNT_FRACTIONS}
            />
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        id: "balance",
        header: t("th_balance_after"),
        headerClassName: "text-right",
        cellClassName: "text-right",
        cell: (leg) => (
          <ColocaAmountDisplay
            value={leg.balance_after}
            size="xs"
            locale={activeLanguage}
            className="text-muted-foreground"
            {...AMOUNT_FRACTIONS}
          />
        ),
      },
    ],
    [activeLanguage, labels, t],
  );

  const totalsRow = (
    <div className="flex flex-col gap-1.5">
      {entry.totals.map((total) => (
        <div key={total.currency} className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
          <span className="text-xs font-medium text-muted-foreground">
            {t("totals_row_label")} · <span className="font-mono">{total.currency}</span>
          </span>
          <span className="flex flex-wrap items-center gap-x-4 gap-y-1">
            <span className="inline-flex items-baseline gap-1.5">
              <span className="text-[11px] text-muted-foreground">{t("totals_debit")}</span>
              <ColocaAmountDisplay
                value={total.debit}
                size="sm"
                locale={activeLanguage}
                {...AMOUNT_FRACTIONS}
              />
            </span>
            <span className="inline-flex items-baseline gap-1.5">
              <span className="text-[11px] text-muted-foreground">{t("totals_credit")}</span>
              <ColocaAmountDisplay
                value={total.credit}
                size="sm"
                locale={activeLanguage}
                {...AMOUNT_FRACTIONS}
              />
            </span>
            <ColocaStatusPill
              variant={total.isBalanced ? "success" : "danger"}
              size="sm"
              icon={total.isBalanced ? Check : TriangleAlert}
            >
              {total.isBalanced ? t("entry_balanced") : t("entry_unbalanced")}
            </ColocaStatusPill>
          </span>
        </div>
      ))}
    </div>
  );

  return (
    <section className={colocaSurfaceVariants({ padding: "none" })}>
      <header className="flex flex-wrap items-start justify-between gap-3 border-b border-border p-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
              {t("entry_index", { index: entry.index })}
            </span>
            <h3 className="text-sm font-semibold text-foreground">{labels.entry(entry)}</h3>
            {entry.reversesEntryIndex != null && (
              <ColocaBadge variant="warning" icon={Undo2} className="font-sans">
                {t("entry_badge_reversal", { index: entry.reversesEntryIndex })}
              </ColocaBadge>
            )}
            {entry.reversedByIndexes.map((index) => (
              <ColocaBadge key={index} variant="secondary" icon={Undo2} className="font-sans">
                {t("entry_badge_reversed", { index })}
              </ColocaBadge>
            ))}
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            {formatDateTime(entry.postedAt, { locale: activeLanguage, includeTime: true })}
          </p>
          {/* On a reversal the description IS the reason the runtime recorded — show it verbatim. */}
          {entry.reversesEntryId && entry.description && (
            <p className="mt-1 text-xs text-foreground">
              {t("entry_reason")}: {entry.description}
            </p>
          )}
        </div>
        <LedgerCopyableId label={t("entry_id_label")} value={entry.entryId} truncate />
      </header>

      {isMobile ? (
        <div className="divide-y divide-border/60">
          {entry.legs.map((leg) => (
            <div key={leg.id} className="space-y-2 p-4">
              <LedgerAccountCell account={leg.account} labels={labels} />
              <div className="flex items-baseline justify-between gap-3">
                <span className="text-xs text-muted-foreground">{labels.direction(leg.direction)}</span>
                <ColocaAmountDisplay
                  value={leg.amount}
                  currency={leg.currency}
                  size="sm"
                  locale={activeLanguage}
                  {...AMOUNT_FRACTIONS}
                />
              </div>
              <div className="flex items-baseline justify-between gap-3">
                <span className="text-xs text-muted-foreground">{t("th_balance_after")}</span>
                <ColocaAmountDisplay
                  value={leg.balance_after}
                  size="xs"
                  locale={activeLanguage}
                  className="text-muted-foreground"
                  {...AMOUNT_FRACTIONS}
                />
              </div>
            </div>
          ))}
          <div className="p-4">{totalsRow}</div>
        </div>
      ) : (
        <ColocaDataTable
          columns={columns}
          rows={entry.legs}
          rowKey={(leg) => leg.id}
          footer={totalsRow}
          // The entry card is the frame; the table only fills it.
          frame="none"
        />
      )}
    </section>
  );
}
